import { createContext, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * GardenTimeContext - Gemeinsamer Garten-Monat (#270 / #608)
 *
 * Ein geteilter Monats-State, den mehrere Ansichten konsumieren:
 * - Blüh-Modus (#608): färbt blühende Pflanzen im gewählten Monat
 * - SunTimeControls / Schatten (#604–#606): Monats-Anteil des Sonnenstands
 * - 3D-Blüh-Schicht (#610): blüht im gewählten Monat
 *
 * „Mein Garten im Juni" ist damit überall Juni. Bewusst **nicht persistiert** —
 * ein View-Zustand, kein Garten-Datum (analog GridContext, aber ohne localStorage).
 *
 * Konvention: `month` ist 0-indiziert (0 = Januar … 11 = Dezember), wie
 * `Date.getMonth()`, damit die Kopplung mit dem Datums-basierten
 * SunTimeControls ohne Umrechnung bleibt. Blüh-Logik rechnet intern auf die
 * 1-basierte `flowering_months`-Domäne um.
 */

const GardenTimeContext = createContext(null);

export const GardenTimeProvider = ({ children, initialMonth }) => {
  const [month, setMonthRaw] = useState(() =>
    initialMonth != null ? clampMonth(initialMonth) : new Date().getMonth()
  );

  const value = useMemo(
    () => ({
      month,
      setMonth: (m) => setMonthRaw(clampMonth(m)),
    }),
    [month]
  );

  return <GardenTimeContext.Provider value={value}>{children}</GardenTimeContext.Provider>;
};

GardenTimeProvider.propTypes = {
  children: PropTypes.node,
  initialMonth: PropTypes.number,
};

/** Auf gültigen 0–11-Monat klemmen; ungültige Eingaben fallen auf Januar. */
function clampMonth(m) {
  const n = Math.trunc(Number(m));
  if (!Number.isFinite(n)) return 0;
  return Math.min(11, Math.max(0, n));
}

/**
 * Datum auf einen anderen Monat setzen, ohne Tages-Überlauf (31. → Kurzmonat).
 * Begleiter der Monats-Kopplung: Konsumenten mit eigenem Datum (Sonnenstand
 * 2D/3D) leiten ihren wirksamen Zeitpunkt aus Kontext-Monat + lokalem
 * Tag/Uhrzeit ab.
 */
export function withMonth(date, targetMonth) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(targetMonth);
  const daysInMonth = new Date(d.getFullYear(), targetMonth + 1, 0).getDate();
  d.setDate(Math.min(date.getDate(), daysInMonth));
  return d;
}

/**
 * useGardenTime - Zugriff auf den geteilten Garten-Monat.
 *
 * @returns {{ month: number, setMonth: (m: number) => void }}
 */
export const useGardenTime = () => {
  const context = useContext(GardenTimeContext);
  if (!context) {
    throw new Error('useGardenTime must be used within a GardenTimeProvider');
  }
  return context;
};

export default GardenTimeContext;
